import { useState, FormEvent, ChangeEvent, useEffect, useRef } from 'react';

type FormData = {
  fullName: string;
  email: string;
  phone: string;
  location: string;
  currentOccupation: string;
  whyEarn: string;
  openToMentorship: string;
  commitTime: string;
  followStructure: string;
  referredBy: string;
  heardFrom: string;
  heardFromOther: string;
  whySelected: string;
  agreeStatement: boolean;
};

type Step = 'form' | 'evaluating' | 'not-qualified' | 'slots' | 'confirmed' | 'error';

const EVALUATION_STEPS = [
  { label: 'Verifying your identity & contact details', icon: 'ri-user-check-line', duration: 6000 },
  { label: 'Assessing financial readiness & motivation', icon: 'ri-money-dollar-circle-line', duration: 8000 },
  { label: 'Evaluating commitment & discipline level', icon: 'ri-shield-check-line', duration: 8000 },
  { label: 'Reviewing referral & documentation', icon: 'ri-team-line', duration: 5000 },
  { label: 'Generating your qualification result', icon: 'ri-award-line', duration: 3000 },
];

/**
 * Aggressively scrolls to the #application-form element.
 * Fires immediately, then retries at 100ms, 300ms, 600ms, and 1000ms
 * to handle cases where the DOM hasn't fully painted yet on mobile.
 */
function enforceScrollToForm() {
  const doScroll = () => {
    const el = document.getElementById('application-form');
    if (el) {
      const rect = el.getBoundingClientRect();
      const absoluteTop = window.pageYOffset + rect.top;
      window.scrollTo({ top: absoluteTop, behavior: 'smooth' });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  doScroll();
  setTimeout(doScroll, 100);
  setTimeout(doScroll, 300);
  setTimeout(doScroll, 600);
  setTimeout(doScroll, 1000);
}

function EvaluatingScreen({ onComplete }: { onComplete: () => void }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);
  const [progress, setProgress] = useState(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const totalDuration = EVALUATION_STEPS.reduce((sum, s) => sum + s.duration, 0);

  useEffect(() => {
    enforceScrollToForm();

    let elapsed = 0;

    const progressInterval = setInterval(() => {
      elapsed += 100;
      setProgress(Math.min((elapsed / totalDuration) * 100, 100));
    }, 100);
    intervalRef.current = progressInterval;

    const runStep = (index: number) => {
      if (index >= EVALUATION_STEPS.length) {
        clearInterval(progressInterval);
        setProgress(100);
        setTimeout(() => onComplete(), 600);
        return;
      }
      setCurrentStep(index);
      timerRef.current = setTimeout(() => {
        setCompletedSteps((prev) => [...prev, index]);
        runStep(index + 1);
      }, EVALUATION_STEPS[index].duration);
    };

    runStep(0);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  return (
    <section id="application-form" className="py-20 bg-white">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-stone-50 rounded-2xl p-10 border border-stone-200 shadow-xl">
          <div className="text-center mb-10">
            <div className="relative w-24 h-24 mx-auto mb-6">
              <svg className="w-24 h-24 -rotate-90" viewBox="0 0 96 96">
                <circle cx="48" cy="48" r="42" fill="none" stroke="#e7e5e4" strokeWidth="6" />
                <circle
                  cx="48" cy="48" r="42"
                  fill="none"
                  stroke="#f59e0b"
                  strokeWidth="6"
                  strokeLinecap="round"
                  strokeDasharray={`${2 * Math.PI * 42}`}
                  strokeDashoffset={`${2 * Math.PI * 42 * (1 - progress / 100)}`}
                  style={{ transition: 'stroke-dashoffset 0.1s linear' }}
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <i className="ri-search-eye-line text-amber-500 text-3xl"></i>
              </div>
            </div>
            <h2
              className="text-2xl sm:text-3xl font-bold text-gray-800 mb-2"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Evaluating Your Application
            </h2>
            <p className="text-gray-500 text-sm max-w-md mx-auto">
              Hold on — our system is carefully reviewing your responses to determine your eligibility.
              This will only take a moment.
            </p>
          </div>

          <div className="w-full bg-stone-200 rounded-full h-2 mb-8 overflow-hidden">
            <div
              className="h-2 bg-amber-500 rounded-full transition-all duration-100"
              style={{ width: `${progress}%` }}
            />
          </div>

          <div className="space-y-4">
            {EVALUATION_STEPS.map((step, i) => {
              const isCompleted = completedSteps.includes(i);
              const isActive = currentStep === i && !isCompleted;
              return (
                <div
                  key={i}
                  className={`flex items-center gap-4 px-5 py-4 rounded-xl border transition-all duration-500 ${
                    isCompleted
                      ? 'border-green-200 bg-green-50'
                      : isActive
                      ? 'border-amber-300 bg-amber-50 shadow-sm'
                      : 'border-stone-200 bg-white opacity-50'
                  }`}
                >
                  <div
                    className={`w-10 h-10 flex items-center justify-center rounded-full flex-shrink-0 ${
                      isCompleted ? 'bg-green-100' : isActive ? 'bg-amber-100' : 'bg-stone-100'
                    }`}
                  >
                    {isCompleted ? (
                      <i className="ri-check-line text-green-600 text-lg"></i>
                    ) : isActive ? (
                      <i className={`${step.icon} text-amber-600 text-lg animate-pulse`}></i>
                    ) : (
                      <i className={`${step.icon} text-stone-400 text-lg`}></i>
                    )}
                  </div>
                  <div className="flex-1">
                    <p
                      className={`text-sm font-semibold ${
                        isCompleted ? 'text-green-700' : isActive ? 'text-amber-700' : 'text-gray-400'
                      }`}
                    >
                      {step.label}
                    </p>
                    {isActive && (
                      <p className="text-xs text-amber-500 mt-0.5 flex items-center gap-1">
                        <i className="ri-loader-4-line animate-spin text-xs"></i> Processing...
                      </p>
                    )}
                    {isCompleted && (
                      <p className="text-xs text-green-500 mt-0.5">Completed</p>
                    )}
                  </div>
                  {isCompleted && (
                    <div className="w-6 h-6 flex items-center justify-center">
                      <i className="ri-checkbox-circle-fill text-green-500 text-xl"></i>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="mt-8 text-center">
            <p className="text-xs text-gray-400 italic">
              Please do not close this page. Your application is being processed securely.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

const EDGE_FUNCTION_BASE = 'https://komdjebsnbdaouylqham.supabase.co/functions/v1';
const SUPABASE_ANON_KEY = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY as string;
const ZOOM_LINK = 'https://us05web.zoom.us/j/7843348764?pwd=MMP....';

const API_HEADERS = {
  'Content-Type': 'application/json',
  Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
};

const initialForm: FormData = {
  fullName: '',
  email: '',
  phone: '',
  location: '',
  currentOccupation: '',
  whyEarn: '',
  openToMentorship: '',
  commitTime: '',
  followStructure: '',
  referredBy: '',
  heardFrom: '',
  heardFromOther: '',
  whySelected: '',
  agreeStatement: false,
};

const LAGOS_LOCATIONS = new Set([
  'lagos',
  'agege', 'ajeromi', 'ifelodun', 'alimosho', 'amuwo-odofin', 'amuwo odofin',
  'apapa', 'badagry', 'epe', 'eti-osa', 'eti osa', 'ibeju', 'lekki',
  'ibeju-lekki', 'ibeju lekki', 'ifako-ijaiye', 'ifako ijaiye', 'ikeja',
  'ikorodu', 'island', 'kosofe', 'lagos island', 'lagos mainland',
  'mushin', 'ojo', 'oshodi', 'isolo', 'oshodi-isolo', 'shomolu',
  'somolu', 'surulere',
  'victoria island', 'vi', 'v.i', 'v.i.', 'ikoyi', 'banana island',
  'lekki phase 1', 'lekki phase1', 'lekki phase 2', 'lekki phase2',
  'ajah', 'sangotedo', 'chevron', 'vgc', 'victoria garden city',
  'festac', 'festac town', 'satellite town', 'mile 2', 'mile2',
  'orile', 'orile agege', 'pen cinema', 'ogba', 'agidingbi',
  'oregun', 'opebi', 'allen', 'allen avenue', 'maryland', 'ojota',
  'ketu', 'mile 12', 'mile12', 'oworo', 'oworonshoki', 'gbagada',
  'pedro', 'bariga', 'akoka', 'yaba', 'sabo', 'ebute metta',
  'ebute-metta', 'otto', 'idumota', 'balogun', 'marina', 'tbs',
  'tinubu', 'broad street', 'obalende', 'onikan', 'iganmu',
  'ijora', 'apongbon', 'costain', 'alaba', 'trade fair',
  'ojo cantonment', 'igbo-elerin', 'igbo elerin', 'iba', 'igando',
  'ikotun', 'egbe', 'idimu', 'ipaja', 'ayobo', 'meiran',
  'abule egba', 'abule-egba', 'iyana ipaja', 'iyana-ipaja',
  'dopemu', 'agbado', 'ojokoro', 'fagba', 'ifako',
  'magodo', 'shangisha', 'ojodu', 'berger', 'omole',
  'isheri', 'isheri north', 'isheri south', 'ojodu berger',
  'ilupeju', 'palmgrove', 'palm grove', 'onipanu', 'fadeyi',
  'jibowu', 'anthony', 'anthony village', 'palmgrove estate',
  'shasha', 'akowonjo', 'egbeda', 'ijesha',
  'lawanson', 'itire', 'coker', 'aguda', 'ojuelegba',
  'eric moore', 'bode thomas', 'adeniran ogunsanya',
  'iponri', 'olodi apapa', 'kirikiri', 'tin can island',
  'badagry expressway', 'okokomaiko', 'volks', 'ilogbo',
  'epe town', 'itoikin', 'agbowa', 'ejirin',
  'eleko', 'akodo', 'eti-osa east', 'eti-osa west',
  'oniru', 'elegushi', 'ilasan', 'jakande', 'osapa',
  'agungi', 'idado', 'oral estate', 'thomas estate',
  'awoyaya', 'bogije', 'lakowe',
  'ikorodu town', 'imota', 'igbogbo', 'ijede', 'bayeku',
  'ikosi', 'alapere', 'agboyi',
  'owode', 'agric', 'agric ikorodu',
]);

function isLagosLocation(location: string): boolean {
  if (!location) return false;
  const normalized = location.toLowerCase().trim();
  if (normalized.includes('lagos')) return true;
  const parts = normalized.split(/[,\/\s]+/).map((p) => p.trim()).filter(Boolean);
  for (const part of parts) {
    if (LAGOS_LOCATIONS.has(part)) return true;
  }
  for (let len = 2; len <= 3; len++) {
    for (let i = 0; i <= parts.length - len; i++) {
      const phrase = parts.slice(i, i + len).join(' ');
      if (LAGOS_LOCATIONS.has(phrase)) return true;
    }
  }
  return false;
}

function isQualified(data: FormData): boolean {
  return (
    data.openToMentorship === 'Yes' &&
    data.commitTime === 'Yes' &&
    data.followStructure === 'Yes, absolutely'
  );
}

function generateSlots(isLagos: boolean): { label: string; date: Date; time: string }[] {
  const slots: { label: string; date: Date; time: string }[] = [];
  const now = new Date();

  if (isLagos) {
    let foundMonday = false;
    let foundTuesday = false;
    const candidate = new Date(now);
    candidate.setHours(0, 0, 0, 0);

    for (let d = 0; d < 30 && (!foundMonday || !foundTuesday); d++) {
      const check = new Date(candidate);
      check.setDate(candidate.getDate() + d);
      const day = check.getDay();

      if (day === 1 || day === 2) {
        const isToday =
          check.getFullYear() === now.getFullYear() &&
          check.getMonth() === now.getMonth() &&
          check.getDate() === now.getDate();

        if (isToday) {
          const isPast11 = now.getHours() > 11 || (now.getHours() === 11 && now.getMinutes() > 0);
          if (isPast11) continue;
        }

        const slot = new Date(check);
        slot.setHours(11, 0, 0, 0);

        if (day === 1 && !foundMonday) {
          foundMonday = true;
          slots.push({
            label: `${slot.toLocaleDateString('en-NG', { weekday: 'long', month: 'long', day: 'numeric' })} — 11:00 AM`,
            date: slot,
            time: '11:00 AM',
          });
        } else if (day === 2 && !foundTuesday) {
          foundTuesday = true;
          slots.push({
            label: `${slot.toLocaleDateString('en-NG', { weekday: 'long', month: 'long', day: 'numeric' })} — 11:00 AM`,
            date: slot,
            time: '11:00 AM',
          });
        }
      }
    }
    slots.sort((a, b) => a.date.getTime() - b.date.getTime());
  } else {
    // Outside Lagos: find next Sunday only (9:00 PM)
    let foundSunday = false;
    const candidate = new Date(now);
    candidate.setHours(0, 0, 0, 0);

    for (let d = 1; d <= 14 && !foundSunday; d++) {
      const check = new Date(candidate);
      check.setDate(candidate.getDate() + d);
      const day = check.getDay();

      if (day === 0 && !foundSunday) {
        // Sunday at 9:00 PM
        const slot = new Date(check);
        slot.setHours(21, 0, 0, 0);
        foundSunday = true;
        slots.push({
          label: `${slot.toLocaleDateString('en-NG', { weekday: 'long', month: 'long', day: 'numeric' })} — 9:00 PM (WAT)`,
          date: slot,
          time: '9:00 PM (WAT)',
        });
      }
    }
  }

  return slots;
}

export default function ApplicationForm() {
  const [formData, setFormData] = useState<FormData>(initialForm);
  const [step, setStep] = useState<Step>('form');
  const [submitting, setSubmitting] = useState(false);
  const [charCount, setCharCount] = useState({ whyEarn: 0, whySelected: 0 });
  const [applicationId, setApplicationId] = useState<string>('');
  const [selectedSlotIndex, setSelectedSlotIndex] = useState<number | null>(null);
  const [bookingSlot, setBookingSlot] = useState(false);
  const [qualifiedResult, setQualifiedResult] = useState(false);
  const [confirmedSlot, setConfirmedSlot] = useState<{ dateLabel: string; time: string; venue: string } | null>(null);

  const slots = step === 'slots' ? generateSlots(isLagosLocation(formData.location)) : [];
  const lagosApplicant = isLagosLocation(formData.location);

  // Enforce scroll every time step changes
  useEffect(() => {
    if (step !== 'form') {
      enforceScrollToForm();
    }
  }, [step]);

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    const checked = type === 'checkbox' ? (e.target as HTMLInputElement).checked : undefined;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    if (name === 'whyEarn' || name === 'whySelected') {
      setCharCount((prev) => ({ ...prev, [name]: value.length }));
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!formData.agreeStatement) return;
    setSubmitting(true);

    const qualified = isQualified(formData);
    setQualifiedResult(qualified);
    const heardFromFinal =
      formData.heardFrom === 'Other' ? `Other: ${formData.heardFromOther}` : formData.heardFrom;

    // Always proceed to evaluation — submission runs in background
    setStep('evaluating');
    setSubmitting(false);

    // Fire-and-forget: save application to backend
    fetch(`${EDGE_FUNCTION_BASE}/select-applicant`, {
      method: 'POST',
      headers: API_HEADERS,
      body: JSON.stringify({
        fullName: formData.fullName,
        email: formData.email,
        phone: formData.phone,
        location: formData.location,
        currentOccupation: formData.currentOccupation,
        whyEarn: formData.whyEarn,
        openToMentorship: formData.openToMentorship,
        commitTime: formData.commitTime,
        followStructure: formData.followStructure,
        referredBy: formData.referredBy,
        heardFrom: heardFromFinal,
        whySelected: formData.whySelected,
        isQualified: qualified,
      }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.applicationId) {
          setApplicationId(data.applicationId);
        }
      })
      .catch((err) => {
        console.warn('Background save failed (non-blocking):', err);
      });
  };

  const handleEvaluationComplete = () => {
    if (qualifiedResult) {
      setStep('slots');
    } else {
      fetch(`${EDGE_FUNCTION_BASE}/send-email`, {
        method: 'POST',
        headers: API_HEADERS,
        body: JSON.stringify({
          type: 'rejection',
          to: formData.email,
          applicantName: formData.fullName,
        }),
      }).catch((err) => console.error('Rejection email failed:', err));

      setStep('not-qualified');
    }
  };

  const handleBookSlot = async () => {
    if (selectedSlotIndex === null) return;
    setBookingSlot(true);

    const slot = slots[selectedSlotIndex];
    const venue = lagosApplicant
      ? '#2 Allen Avenue, Gabriel Akinmade Taylor Plaza, Near Toyin Roundabout, Ikeja. 3rd Floor, Winning Life Center'
      : ZOOM_LINK;

    const appointmentDateFormatted = slot.date.toLocaleDateString('en-NG', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });

    setConfirmedSlot({
      dateLabel: appointmentDateFormatted,
      time: slot.time,
      venue: lagosApplicant
        ? '#2 Allen Avenue, Gabriel Akinmade Taylor Plaza, Near Toyin Roundabout, Ikeja. 3rd Floor, Winning Life Center'
        : 'Online via Zoom',
    });

    try {
      await fetch(`${EDGE_FUNCTION_BASE}/select-applicant`, {
        method: 'POST',
        headers: API_HEADERS,
        body: JSON.stringify({
          action: 'book_slot',
          applicationId,
          appointmentDate: slot.date.toISOString(),
          appointmentTime: slot.time,
          venue,
          isLagos: lagosApplicant,
          applicantName: formData.fullName,
          email: formData.email,
          phone: formData.phone,
        }),
      });

      fetch(`${EDGE_FUNCTION_BASE}/send-email`, {
        method: 'POST',
        headers: API_HEADERS,
        body: JSON.stringify({
          type: 'booking_confirmation',
          to: formData.email,
          applicantName: formData.fullName,
          appointmentDate: slot.date.toISOString(),
          isLagos: lagosApplicant,
          zoomLink: lagosApplicant ? null : ZOOM_LINK,
        }),
      }).catch((err) => console.error('Booking confirmation email failed:', err));

      fetch(`${EDGE_FUNCTION_BASE}/send-email`, {
        method: 'POST',
        headers: API_HEADERS,
        body: JSON.stringify({
          type: 'admin_notification',
          to: 'themonthlymillionairesproject@gmail.com',
          applicantName: formData.fullName,
          applicantEmail: formData.email,
          applicantPhone: formData.phone,
          location: formData.location,
          occupation: formData.currentOccupation,
          referredBy: formData.referredBy,
          heardFrom: formData.heardFrom === 'Other' ? `Other: ${formData.heardFromOther}` : formData.heardFrom,
          appointmentDate: slot.date.toISOString(),
          isLagos: lagosApplicant,
        }),
      }).catch((err) => console.error('Admin email notification failed:', err));

      fetch(`${EDGE_FUNCTION_BASE}/send-whatsapp`, {
        method: 'POST',
        headers: API_HEADERS,
        body: JSON.stringify({
          to: formData.phone,
          applicantName: formData.fullName,
          appointmentDate: appointmentDateFormatted,
          appointmentTime: slot.time,
          venue: lagosApplicant
            ? '#2 Allen Avenue, Ikeja, Lagos - 3rd Floor, Winning Life Center'
            : `Zoom Meeting: ${ZOOM_LINK}`,
          isAdmin: false,
        }),
      }).catch((err) => console.error('WhatsApp appointment notification failed:', err));

      fetch(`${EDGE_FUNCTION_BASE}/send-whatsapp`, {
        method: 'POST',
        headers: API_HEADERS,
        body: JSON.stringify({
          to: '+2349137897299',
          applicantName: formData.fullName,
          appointmentDate: appointmentDateFormatted,
          appointmentTime: slot.time,
          venue: lagosApplicant
            ? '#2 Allen Avenue, Ikeja, Lagos - 3rd Floor, Winning Life Center'
            : `Zoom Meeting: ${ZOOM_LINK}`,
          isAdmin: true,
        }),
      }).catch((err) => console.error('Admin WhatsApp notification failed:', err));

    } catch (err) {
      console.error('Slot booking error:', err);
    }

    setBookingSlot(false);
    setStep('confirmed');
  };

  const inputClass =
    'w-full px-4 py-3 bg-white border border-stone-300 rounded-lg text-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent';
  const labelClass = 'block text-sm font-semibold text-gray-700 mb-2';
  const radioClass = 'flex items-center gap-2 cursor-pointer text-sm text-gray-800';
  const sectionHeaderClass =
    'flex items-center gap-2 text-amber-600 font-bold text-xs uppercase tracking-widest mb-5 pb-2 border-b border-amber-200';

  // ── EVALUATING ─────────────────────────────────────────────────────────────
  if (step === 'evaluating') {
    return <EvaluatingScreen onComplete={handleEvaluationComplete} />;
  }

  // ── NOT QUALIFIED ──────────────────────────────────────────────────────────
  if (step === 'not-qualified') {
    return (
      <section id="application-form" className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-stone-50 rounded-2xl p-10 border border-stone-200 shadow-xl text-center">
            <div className="w-20 h-20 flex items-center justify-center bg-amber-100 rounded-full mx-auto mb-6">
              <i className="ri-time-line text-amber-500 text-5xl"></i>
            </div>
            <h3
              className="text-2xl sm:text-3xl font-bold text-gray-800 mb-3"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Thank You, {formData.fullName.split(' ')[0]}
            </h3>
            <div className="w-12 h-0.5 bg-amber-400 mx-auto mb-6"></div>
            <p className="text-gray-600 text-base mb-6 leading-relaxed max-w-lg mx-auto">
              Your application has been received. Based on your responses, we&apos;ll be in touch if a suitable
              opportunity arises.
            </p>
            <div className="bg-amber-50 border border-amber-200 rounded-xl px-6 py-5 mb-8 text-left max-w-lg mx-auto">
              <p className="text-amber-800 text-sm font-bold mb-2 flex items-center gap-2">
                <i className="ri-information-line text-lg"></i> What Happens Next?
              </p>
              <p className="text-amber-700 text-sm leading-relaxed">
                Our team reviews all applications. Confirmation details will be sent to your email at{' '}
                <strong>{formData.email}</strong>.
              </p>
            </div>
            <p className="text-amber-600 font-bold text-base mb-1">Stay ready.</p>
            <p className="text-gray-500 text-sm">— Monthly Millionaires Project Team</p>
            <button
              onClick={() => {
                setStep('form');
                setFormData(initialForm);
                setCharCount({ whyEarn: 0, whySelected: 0 });
              }}
              className="mt-8 px-6 py-3 bg-amber-500 text-white rounded-lg text-sm font-semibold hover:bg-amber-600 transition-all whitespace-nowrap cursor-pointer"
            >
              Submit Another Application
            </button>
          </div>
        </div>
      </section>
    );
  }

  // ── SLOT SELECTION ─────────────────────────────────────────────────────────
  if (step === 'slots') {
    return (
      <section id="application-form" className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <div className="w-16 h-16 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-4">
              <i className="ri-checkbox-circle-fill text-green-500 text-4xl"></i>
            </div>
            <span className="inline-block bg-green-100 text-green-700 text-xs font-bold uppercase tracking-widest px-4 py-1 rounded-full mb-3">
              ✅ You Qualify!
            </span>
            <h2
              className="text-3xl sm:text-4xl font-bold text-gray-800 mb-3"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Select Your Appointment Slot
            </h2>
            <p className="text-gray-500 text-sm max-w-xl mx-auto">
              Congratulations, <strong>{formData.fullName.split(' ')[0]}</strong>! You&apos;ve passed the
              screening. Choose a time that works best for you below.
            </p>
          </div>

          <div className="bg-stone-50 rounded-2xl p-8 border border-stone-200 shadow-xl">
            <div
              className={`rounded-xl p-5 mb-8 flex items-start gap-4 ${
                lagosApplicant ? 'bg-amber-50 border border-amber-200' : 'bg-stone-800'
              }`}
            >
              <div
                className={`w-10 h-10 flex items-center justify-center rounded-full flex-shrink-0 ${
                  lagosApplicant ? 'bg-amber-200' : 'bg-amber-500'
                }`}
              >
                <i
                  className={`text-lg ${
                    lagosApplicant ? 'ri-map-pin-line text-amber-700' : 'ri-video-line text-white'
                  }`}
                ></i>
              </div>
              <div>
                <p
                  className={`text-xs font-bold uppercase tracking-widest mb-1 ${
                    lagosApplicant ? 'text-amber-700' : 'text-amber-400'
                  }`}
                >
                  {lagosApplicant ? 'Physical Meeting — Lagos Office' : 'Online Meeting — Zoom'}
                </p>
                {lagosApplicant ? (
                  <p className="text-amber-800 text-sm leading-relaxed">
                    #2 Allen Avenue, Gabriel Akinmade Taylor Plaza, Near Toyin Roundabout, Ikeja.
                    <br />
                    <strong>3rd Floor, Winning Life Center</strong>
                  </p>
                ) : (
                  <p className="text-gray-300 text-sm leading-relaxed">
                    You will join via Zoom. The link will be included in your confirmation email.
                    <br />
                    <span className="text-amber-400 font-semibold">
                      Please ensure you have a stable internet connection.
                    </span>
                  </p>
                )}
              </div>
            </div>

            <p className={sectionHeaderClass}>
              <i className="ri-calendar-line text-base"></i> Available Time Slots
            </p>
            <div className="space-y-3 mb-8">
              {slots.map((slot, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setSelectedSlotIndex(i)}
                  className={`w-full flex items-center gap-4 px-5 py-4 rounded-xl border-2 transition-all cursor-pointer text-left ${
                    selectedSlotIndex === i
                      ? 'border-amber-500 bg-amber-50 shadow-md'
                      : 'border-stone-200 bg-white hover:border-amber-300'
                  }`}
                >
                  <div
                    className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                      selectedSlotIndex === i ? 'border-amber-500 bg-amber-500' : 'border-stone-300'
                    }`}
                  >
                    {selectedSlotIndex === i && (
                      <i className="ri-check-line text-white text-xs"></i>
                    )}
                  </div>
                  <div>
                    <p
                      className={`text-sm font-semibold ${
                        selectedSlotIndex === i ? 'text-amber-700' : 'text-gray-800'
                      }`}
                    >
                      {slot.label}
                    </p>
                    <p className="text-xs text-gray-400 mt-0.5">
                      {lagosApplicant ? 'Physical — Ikeja, Lagos' : 'Online — Zoom'}
                    </p>
                  </div>
                </button>
              ))}
            </div>

            <button
              onClick={handleBookSlot}
              disabled={selectedSlotIndex === null || bookingSlot}
              className="w-full bg-amber-500 text-white px-8 py-4 rounded-lg text-base font-bold hover:bg-amber-600 transition-all duration-300 transform hover:scale-105 shadow-lg whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none cursor-pointer"
            >
              {bookingSlot ? (
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-loader-4-line animate-spin"></i> Confirming...
                </span>
              ) : (
                'Confirm Appointment'
              )}
            </button>
          </div>
        </div>
      </section>
    );
  }

  // ── CONFIRMED ──────────────────────────────────────────────────────────────
  if (step === 'confirmed') {
    return (
      <section id="application-form" className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-stone-50 rounded-2xl p-10 border border-stone-200 shadow-xl text-center">
            <div className="w-20 h-20 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-6">
              <i className="ri-checkbox-circle-fill text-green-500 text-5xl"></i>
            </div>
            <h3
              className="text-2xl sm:text-3xl font-bold text-gray-800 mb-3"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Appointment Confirmed!
            </h3>
            <div className="w-12 h-0.5 bg-amber-400 mx-auto mb-6"></div>

            <p className="text-gray-700 text-base mb-6 leading-relaxed font-medium max-w-lg mx-auto">
              Well done, <strong>{formData.fullName.split(' ')[0]}</strong>. Your appointment has been
              booked successfully.
            </p>

            {confirmedSlot && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl px-6 py-5 mb-6 text-left max-w-lg mx-auto">
                <p className="text-amber-800 text-sm font-bold mb-3 flex items-center gap-2">
                  <i className="ri-calendar-check-line text-lg"></i> Your Appointment Details
                </p>
                <div className="space-y-2">
                  <p className="text-amber-700 text-sm">
                    <strong>Date:</strong> {confirmedSlot.dateLabel}
                  </p>
                  <p className="text-amber-700 text-sm">
                    <strong>Time:</strong> {confirmedSlot.time}
                  </p>
                  <p className="text-amber-700 text-sm">
                    <strong>Venue:</strong> {confirmedSlot.venue}
                  </p>
                </div>
              </div>
            )}

            {lagosApplicant && (
              <div className="bg-green-50 border border-green-200 rounded-xl px-6 py-4 mb-6 text-left max-w-lg mx-auto">
                <p className="text-green-800 text-sm leading-relaxed">
                  <strong>Please arrive at least 10 minutes early.</strong> Come with a notebook and writing materials.
                </p>
              </div>
            )}

            <div className="bg-amber-50 border border-amber-200 rounded-xl px-6 py-5 mb-6 text-left max-w-lg mx-auto">
              <p className="text-amber-800 text-sm font-bold mb-2 flex items-center gap-2">
                <i className="ri-mail-line text-lg"></i> What Happens Next?
              </p>
              <p className="text-amber-700 text-sm leading-relaxed">
                A confirmation email with all your appointment details has been sent to{' '}
                <strong>{formData.email}</strong>. You will also receive a reminder before your
                appointment. Please keep your email and WhatsApp active.
              </p>
            </div>

            <div className="bg-stone-800 rounded-xl px-6 py-5 max-w-lg mx-auto mb-8">
              <p className="text-amber-400 text-xs font-bold uppercase tracking-widest mb-2">
                Important
              </p>
              <p className="text-gray-300 text-sm leading-relaxed">
                This is a structured mentorship program — not a get-rich-quick scheme. Please be
                punctual for your appointment.
              </p>
            </div>

            <div className="bg-green-50 border-2 border-green-200 rounded-xl px-6 py-5 max-w-lg mx-auto mb-8">
              <p className="text-green-800 text-sm font-bold mb-3 flex items-center justify-center gap-2">
                <i className="ri-question-answer-line text-lg"></i> Have Questions?
              </p>
              <p className="text-green-700 text-sm leading-relaxed mb-4">
                If you have any questions or need assistance, feel free to reach out to us directly on WhatsApp.
              </p>
              <a
                href="https://wa.me/2349137897299"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-green-600 text-white px-6 py-3 rounded-lg text-sm font-semibold hover:bg-green-700 transition-all whitespace-nowrap cursor-pointer w-full"
              >
                <i className="ri-whatsapp-line text-lg"></i>
                Contact Us on WhatsApp
              </a>
            </div>

            <p className="text-amber-600 font-bold text-base mb-1">Welcome to the Monthly Millionaires Project.</p>
            <p className="text-gray-500 text-sm">— The Monthly Millionaires Project Team</p>

            <button
              onClick={() => {
                setStep('form');
                setFormData(initialForm);
                setCharCount({ whyEarn: 0, whySelected: 0 });
                setSelectedSlotIndex(null);
                setConfirmedSlot(null);
              }}
              className="mt-8 px-6 py-3 bg-amber-500 text-white rounded-lg text-sm font-semibold hover:bg-amber-600 transition-all whitespace-nowrap cursor-pointer"
            >
              Submit Another Application
            </button>
          </div>
        </div>
      </section>
    );
  }

  // ── ERROR ──────────────────────────────────────────────────────────────────
  if (step === 'error') {
    return (
      <section id="application-form" className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-stone-50 rounded-2xl p-10 border border-stone-200 shadow-xl text-center">
            <div className="w-16 h-16 flex items-center justify-center bg-red-100 rounded-full mx-auto mb-6">
              <i className="ri-error-warning-fill text-red-500 text-4xl"></i>
            </div>
            <h3
              className="text-2xl font-bold text-gray-800 mb-3"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Something Went Wrong
            </h3>
            <p className="text-gray-600 text-sm mb-6 max-w-md mx-auto">
              We couldn&apos;t process your application. Please try again.
            </p>
            <button
              onClick={() => setStep('form')}
              className="px-6 py-3 bg-amber-500 text-white rounded-lg text-sm font-semibold hover:bg-amber-600 transition-all whitespace-nowrap cursor-pointer"
            >
              Try Again
            </button>
          </div>
        </div>
      </section>
    );
  }

  // ── FORM ───────────────────────────────────────────────────────────────────
  return (
    <section id="application-form" className="py-20 bg-white">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <span className="inline-block bg-amber-100 text-amber-700 text-xs font-bold uppercase tracking-widest px-4 py-1 rounded-full mb-4">
            Application &amp; Screening Form
          </span>
          <h2
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-800 mb-4"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            Apply for the Monthly Millionaires Project
          </h2>
          <p className="text-gray-500 text-sm max-w-xl mx-auto">
            Applications are reviewed. This platform is not open to everyone. Only serious and
            qualified applicants will be shortlisted.
          </p>
        </div>

        <div className="bg-stone-50 rounded-2xl p-8 border border-stone-200 shadow-xl">
          <form
            id="mmp-application-form"
            data-readdy-form
            onSubmit={handleSubmit}
            className="space-y-8"
          >
            {/* SECTION 1 */}
            <div>
              <p className={sectionHeaderClass}>
                <i className="ri-user-line text-base"></i> Section 1: Basic Information
              </p>
              <div className="space-y-5">
                <div>
                  <label htmlFor="fullName" className={labelClass}>
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="fullName"
                    name="fullName"
                    required
                    value={formData.fullName}
                    onChange={handleChange}
                    className={inputClass}
                    placeholder="Enter your full name"
                  />
                </div>
                <div>
                  <label htmlFor="email" className={labelClass}>
                    Email Address <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className={inputClass}
                    placeholder="Enter your email address"
                  />
                </div>
                <div>
                  <label htmlFor="phone" className={labelClass}>
                    Phone Number (WhatsApp active) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    required
                    value={formData.phone}
                    onChange={handleChange}
                    className={inputClass}
                    placeholder="+234"
                  />
                </div>
                <div>
                  <label htmlFor="location" className={labelClass}>
                    Current Location (City &amp; State) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="location"
                    name="location"
                    required
                    value={formData.location}
                    onChange={handleChange}
                    className={inputClass}
                    placeholder="e.g. Ikeja, Lagos"
                  />
                </div>
                <div>
                  <label className={labelClass}>
                    What do you currently do? <span className="text-red-500">*</span>
                  </label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {['Employed', 'Self-Employed', 'Business Owner', 'Student', 'Currently Unemployed'].map((opt) => (
                      <label
                        key={opt}
                        className={`${radioClass} bg-white border border-stone-200 rounded-lg px-3 py-2 hover:border-amber-400 transition-colors`}
                      >
                        <input
                          type="radio"
                          name="currentOccupation"
                          value={opt}
                          checked={formData.currentOccupation === opt}
                          onChange={handleChange}
                          required
                          className="accent-amber-500 w-4 h-4 shrink-0"
                        />
                        <span>{opt}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* SECTION 2 */}
            <div>
              <p className={sectionHeaderClass}>
                <i className="ri-money-dollar-circle-line text-base"></i> Section 2: Financial Readiness
              </p>
              <div className="space-y-5">
                <div>
                  <label htmlFor="whyEarn" className={labelClass}>
                    Why are you interested in earning ₦1,000,000+ monthly?{' '}
                    <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="whyEarn"
                    name="whyEarn"
                    required
                    value={formData.whyEarn}
                    onChange={handleChange}
                    maxLength={500}
                    rows={4}
                    className={`${inputClass} resize-none`}
                    placeholder="Share your honest reason and motivation..."
                  ></textarea>
                  <p className="text-xs text-gray-400 text-right mt-1">{charCount.whyEarn}/500</p>
                </div>
                <div>
                  <label className={labelClass}>
                    Are you open to mentorship and structured business systems?{' '}
                    <span className="text-red-500">*</span>
                  </label>
                  <div className="flex gap-4">
                    {['Yes', 'No'].map((opt) => (
                      <label
                        key={opt}
                        className={`${radioClass} bg-white border border-stone-200 rounded-lg px-4 py-2 hover:border-amber-400 transition-colors`}
                      >
                        <input
                          type="radio"
                          name="openToMentorship"
                          value={opt}
                          checked={formData.openToMentorship === opt}
                          onChange={handleChange}
                          required
                          className="accent-amber-500 w-4 h-4 shrink-0"
                        />
                        <span>{opt}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div>
                  <label className={labelClass}>
                    If selected, are you willing to commit time weekly to building a structured
                    income system? <span className="text-red-500">*</span>
                  </label>
                  <div className="flex gap-4">
                    {['Yes', 'Not Sure'].map((opt) => (
                      <label
                        key={opt}
                        className={`${radioClass} bg-white border border-stone-200 rounded-lg px-4 py-2 hover:border-amber-400 transition-colors`}
                      >
                        <input
                          type="radio"
                          name="commitTime"
                          value={opt}
                          checked={formData.commitTime === opt}
                          onChange={handleChange}
                          required
                          className="accent-amber-500 w-4 h-4 shrink-0"
                        />
                        <span>{opt}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* SECTION 3 */}
            <div>
              <p className={sectionHeaderClass}>
                <i className="ri-shield-check-line text-base"></i> Section 3: Commitment Filter
              </p>
              <div>
                <label className={labelClass}>
                  This project requires discipline, regimentation, and consistency. Are you willing
                  to follow a proven structure without shortcuts?{' '}
                  <span className="text-red-500">*</span>
                </label>
                <div className="flex flex-col sm:flex-row gap-3">
                  {['Yes, absolutely', 'I prefer doing things my own way'].map((opt) => (
                    <label
                      key={opt}
                      className={`${radioClass} bg-white border border-stone-200 rounded-lg px-4 py-3 hover:border-amber-400 transition-colors flex-1`}
                    >
                      <input
                        type="radio"
                        name="followStructure"
                        value={opt}
                        checked={formData.followStructure === opt}
                        onChange={handleChange}
                        required
                        className="accent-amber-500 w-4 h-4 shrink-0"
                      />
                      <span>{opt}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            {/* SECTION 4 */}
            <div>
              <p className={sectionHeaderClass}>
                <i className="ri-team-line text-base"></i> Section 4: Referral &amp; Documentation
              </p>
              <div className="space-y-5">
                <div>
                  <label htmlFor="referredBy" className={labelClass}>
                    Full name of the person who introduced you to the Monthly Millionaires Project{' '}
                    <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="referredBy"
                    name="referredBy"
                    required
                    value={formData.referredBy}
                    onChange={handleChange}
                    className={inputClass}
                    placeholder="Enter their full name"
                  />
                </div>
                <div>
                  <label className={labelClass}>
                    Where did you first hear about this opportunity?{' '}
                    <span className="text-red-500">*</span>
                  </label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {[
                      'Facebook Post',
                      'Facebook Group',
                      'WhatsApp',
                      'Instagram',
                      'Business Briefing / Seminar',
                      'Direct Invitation',
                      'Other',
                    ].map((opt) => (
                      <label
                        key={opt}
                        className={`${radioClass} bg-white border border-stone-200 rounded-lg px-3 py-2 hover:border-amber-400 transition-colors`}
                      >
                        <input
                          type="radio"
                          name="heardFrom"
                          value={opt}
                          checked={formData.heardFrom === opt}
                          onChange={handleChange}
                          required
                          className="accent-amber-500 w-4 h-4 shrink-0"
                        />
                        <span>{opt}</span>
                      </label>
                    ))}
                  </div>
                  {formData.heardFrom === 'Other' && (
                    <input
                      type="text"
                      name="heardFromOther"
                      value={formData.heardFromOther}
                      onChange={handleChange}
                      className={`${inputClass} mt-3`}
                      placeholder="Please specify..."
                      required
                    />
                  )}
                </div>
              </div>
            </div>

            {/* SECTION 5 */}
            <div>
              <p className={sectionHeaderClass}>
                <i className="ri-award-line text-base"></i> Section 5: Qualification Positioning
              </p>
              <div>
                <label htmlFor="whySelected" className={labelClass}>
                  Why should you be selected for this project?{' '}
                  <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="whySelected"
                  name="whySelected"
                  required
                  value={formData.whySelected}
                  onChange={handleChange}
                  maxLength={500}
                  rows={4}
                  className={`${inputClass} resize-none`}
                  placeholder="Make your case — tell us why you deserve a spot..."
                ></textarea>
                <p className="text-xs text-gray-400 text-right mt-1">{charCount.whySelected}/500</p>
              </div>
            </div>

            {/* FINAL CONFIRMATION */}
            <div className="bg-gray-800 rounded-xl p-6 text-white">
              <p className="text-sm leading-relaxed mb-4 text-gray-300 italic">
                &quot;I understand that this is not a get-rich-quick scheme. I am applying to be
                considered for mentorship and structured income training under the Monthly Millionaires
                Project.&quot;
              </p>
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  name="agreeStatement"
                  checked={formData.agreeStatement}
                  onChange={handleChange}
                  required
                  className="accent-amber-500 w-5 h-5 mt-0.5 shrink-0 cursor-pointer"
                />
                <span className="text-sm font-semibold text-amber-400">
                  I Agree — I confirm the statement above and I am ready to be considered.
                </span>
              </label>
            </div>

            <button
              type="submit"
              disabled={submitting || !formData.agreeStatement}
              className="w-full bg-amber-500 text-white px-8 py-4 rounded-lg text-base font-bold hover:bg-amber-600 transition-all duration-300 transform hover:scale-105 shadow-lg whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none cursor-pointer"
            >
              {submitting ? (
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-loader-4-line animate-spin"></i> Processing...
                </span>
              ) : (
                'Submit Application'
              )}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}
